import { useRef, useEffect, useLayoutEffect, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

export type ContextMenuEntry =
  | {
    readonly id: string;
    readonly label: string;
    readonly icon?: IconName;
    readonly variant?: "default" | "danger";
    readonly colorDot?: string;
    readonly role?: "menuitem" | "menuitemradio";
    readonly ariaChecked?: boolean;
  }
  | { readonly type: "separator" }
  | { readonly type: "submenu"; readonly label: string; readonly icon: IconName; readonly children: readonly ContextMenuEntry[] };

export interface ContextMenuProps {
  readonly items: readonly ContextMenuEntry[];
  readonly position: { readonly x: number; readonly y: number };
  readonly onSelect: (itemId: string) => void;
  readonly onClose: () => void;
}

interface ContextMenuSurfaceProps {
  readonly items: readonly ContextMenuEntry[];
  readonly onSelect: (itemId: string) => void;
  readonly header?: string;
  readonly footer?: ReactNode;
  readonly className?: string;
}

function SubmenuItem({
  entry,
  onSelect,
}: {
  readonly entry: { readonly label: string; readonly icon: IconName; readonly children: readonly ContextMenuEntry[] };
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(true);
  };
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleLeave = () => {
    timerRef.current = setTimeout(() => { setOpen(false); }, 150);
  };

  // Position submenu to the right, clamped to viewport
  const [subPos, setSubPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !itemRef.current || !subRef.current) return;
    const parentRect = itemRef.current.getBoundingClientRect();
    const subRect = subRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = parentRect.right + 2;
    let top = parentRect.top;
    if (left + subRect.width > vw) left = parentRect.left - subRect.width - 2;
    if (top + subRect.height > vh) top = vh - subRect.height - 4;
    if (top < 4) top = 4;
    setSubPos({ left, top });
  }, [open]);

  return (
    <div
      ref={itemRef}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className="flex w-full items-center gap-3 px-4 py-1.5 text-left text-content transition-colors duration-100 hover:bg-surface-hover cursor-default">
        <Icon name={entry.icon} size={18} className="text-content-secondary" />
        <span className="text-[14px] font-normal flex-1">{entry.label}</span>
        <Icon name="chevron-right" size={14} className="text-content-tertiary" />
      </div>
      {open && entry.children.length > 0 && (
        <div
          ref={subRef}
          className="fixed z-[51] min-w-[190px] rounded-2xl bg-surface-tertiary py-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.65)]"
          style={subPos ? { left: subPos.left, top: subPos.top, opacity: 1 } : { left: 0, top: 0, opacity: 0 }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {entry.children.map((child, ci) => {
            if ("type" in child && child.type === "separator") {
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              return <div key={`sub-sep-${ci}`} className="mx-3 my-1 border-t border-edge" />;
            }
            if ("type" in child) return null;
            const c = child as { id: string; label: string; icon: IconName; colorDot?: string };
            return (
              <button
                key={c.id}
                type="button"
                className="flex w-full items-center gap-3 px-4 py-1.5 text-left text-content transition-colors duration-100 hover:bg-surface-hover"
                onClick={() => { onSelect(c.id); }}
              >
                {c.colorDot ? (
                  <span
                    className="inline-block w-[10px] h-[10px] rounded-full shrink-0"
                    style={{ backgroundColor: c.colorDot }}
                  />
                ) : (
                  <Icon name={c.icon} size={18} className="text-content-secondary" />
                )}
                <span className="text-[14px] font-normal">{c.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ContextMenu({ items, position, onSelect, onClose }: ContextMenuProps): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState<{ x: number; y: number } | null>(null);

  // Measure and clamp to viewport edges
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = position.x;
    let y = position.y;
    if (x + rect.width > vw) x = vw - rect.width - 4;
    if (y + rect.height > vh) y = vh - rect.height - 4;
    if (x < 4) x = 4;
    if (y < 4) y = 4;
    setAdjusted({ x, y });
  }, [position.x, position.y]);

  // Auto-dismiss listeners
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleScroll = () => { onClose(); };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleResize = () => { onClose(); };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [onClose]);

  const pos = adjusted ?? position;

  return (
    <div
      ref={menuRef}
      className="fixed z-50"
      style={{
        left: pos.x,
        top: pos.y,
        opacity: adjusted ? 1 : 0,
      }}
    >
      <ContextMenuSurface items={items} onSelect={onSelect} />
    </div>
  );
}

export function ContextMenuSurface({
  items,
  onSelect,
  header,
  footer,
  className,
}: ContextMenuSurfaceProps): JSX.Element {
  return (
    <div
      className={`min-w-[210px] rounded-2xl bg-surface-tertiary py-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.65)] ${className ?? ""}`.trim()}
    >
      {header ? (
        <div className="px-4 pb-1 pt-1 text-xs font-medium uppercase tracking-[0.12em] text-content-secondary">
          {header}
        </div>
      ) : null}
      {items.map((item, i) => {
        if ("type" in item && item.type === "separator") {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          return <div key={`sep-${i}`} className="mx-3 my-1 border-t border-edge" />;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if ("type" in item && item.type === "submenu") {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          return <SubmenuItem key={`sub-${i}`} entry={item} onSelect={onSelect} />;
        }

        const entry = item as {
          id: string;
          label: string;
          icon?: IconName;
          variant?: "default" | "danger";
          colorDot?: string;
          role?: "menuitem" | "menuitemradio";
          ariaChecked?: boolean;
        };
        const isDanger = entry.variant === "danger";

        return (
          <button
            key={entry.id}
            type="button"
            role={entry.role}
            aria-checked={entry.ariaChecked}
            className={`flex w-full items-center gap-3 px-4 py-1.5 text-left transition-colors duration-100 hover:bg-surface-hover ${
              isDanger ? "text-red-400" : "text-content"
            }`}
            onClick={() => { onSelect(entry.id); }}
          >
            {entry.colorDot ? (
              <span
                className="inline-block h-[10px] w-[10px] rounded-full shrink-0"
                style={{ backgroundColor: entry.colorDot }}
              />
            ) : entry.icon ? (
              <Icon
                name={entry.icon}
                size={18}
                className={isDanger ? "text-red-400" : "text-content-secondary"}
              />
            ) : (
              <span className="inline-block h-[18px] w-[18px] shrink-0" aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1 truncate text-[14px] font-normal">{entry.label}</span>
          </button>
        );
      })}
      {footer}
    </div>
  );
}
